import { supabase } from '../lib/supabase';

export interface SessionSummaryRecord {
  id?: string;
  user_id: string;
  specialty_name: string;
  topic_name: string;
  ips_score: number | null;
  data_gathering_score: number | null;
  diagnosis_mgt_score: number | null;
  areas_for_improvement: string;
  created_at?: string;
}

/**
 * Save a session summary to Supabase.
 * Falls back silently if the table doesn't exist yet (user hasn't run migration).
 */
export async function saveSessionSummary(
  userId: string,
  summary: {
    specialtyName: string;
    topicName: string;
    ipsScore: number | null;
    dataGatheringScore: number | null;
    diagnosisMgtScore: number | null;
    areasForImprovement: string;
  }
): Promise<boolean> {
  try {
    const { error } = await supabase.from('session_summaries').insert({
      user_id: userId,
      specialty_name: summary.specialtyName,
      topic_name: summary.topicName,
      ips_score: summary.ipsScore,
      data_gathering_score: summary.dataGatheringScore,
      diagnosis_mgt_score: summary.diagnosisMgtScore,
      areas_for_improvement: summary.areasForImprovement,
    });

    if (error) {
      console.error('Error saving session summary to Supabase:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('Failed to save session summary:', err);
    return false;
  }
}

/**
 * Fetch all session summaries for a user from Supabase.
 * Returns empty array if the table doesn't exist or there's an error.
 */
export async function getSessionHistory(
  userId: string
): Promise<SessionSummaryRecord[]> {
  try {
    const { data, error } = await supabase
      .from('session_summaries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching session history from Supabase:', error);
      return [];
    }
    return (data || []) as SessionSummaryRecord[];
  } catch (err) {
    console.error('Failed to fetch session history:', err);
    return [];
  }
}
